import { IconSearch } from '@/app/components/atoms/Icons'
import { cx } from '@/app/lib/utils'

import {
  DEFAULT_ICON_SIZE,
  DEFAULT_TITLE_LEVEL,
  DEFAULT_TONE,
} from './EmptyState.constants'
import styles from './EmptyState.module.scss'
import type { EmptyStateProps } from './EmptyState.types'

/**
 * Estado vacío: icono/ilustración + título + descripción y una acción opcional.
 * Para listas vacías o resultados de búsqueda sin coincidencias.
 *
 * Server Component: solo presenta contenido (sin hooks, eventos ni APIs de
 * navegador). La interactividad (p. ej. un botón de "reintentar") la aporta el
 * consumidor a través del slot `action`, normalmente con `atoms/Button`.
 *
 * Accesibilidad:
 * - El título usa un heading semántico cuyo nivel controla el consumidor con
 *   `titleAs` para respetar la jerarquía de la página (WCAG 2.1 AA — 1.3.1).
 * - El icono/ilustración es decorativo (`aria-hidden`): el mensaje lo aporta el
 *   texto, por lo que no se duplica para lectores de pantalla.
 */
export function EmptyState({
  title,
  titleAs = DEFAULT_TITLE_LEVEL,
  description,
  icon,
  action,
  tone = DEFAULT_TONE,
  className,
  style,
}: EmptyStateProps) {
  const Heading = `h${titleAs}` as const

  return (
    <div className={cx(styles.root, className)} style={style} data-tone={tone}>
      <span className={styles.icon} aria-hidden="true">
        {icon ?? <IconSearch size={DEFAULT_ICON_SIZE} />}
      </span>

      <Heading className={styles.title}>{title}</Heading>

      {description && <p className={styles.description}>{description}</p>}

      {action && <div className={styles.action}>{action}</div>}
    </div>
  )
}
